import { useTranslation } from 'react-i18next';

import {
  TableChartConfigDto,
  BatterSingleSessionPitchByPitchStatsChartDataDto,
  BatterSingleSessionPitchByPitchStatsDto,
} from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { useMemo, useState } from 'react';
import Table, { Order } from 'components/Table/Table';
import { getDisplayedColumns, tableHeaders } from 'utilities/tableHeaders';
import PitchTypeTableCell from 'components/Table/PitchTypeTableCell';
import PitchLocationTableCell from 'components/Table/PitchLocationTableCell';

export type BatterSingleSessionPitchByPitchStatsChartColumn =
  keyof BatterSingleSessionPitchByPitchStatsChartDataDto['pitchByPitchStats'][number];

const BatterSingleSessionPitchByPitchStatsChart = ({
  chartData,
  isLoading,
  chartConfig,
}: ChartProps<BatterSingleSessionPitchByPitchStatsChartDataDto, TableChartConfigDto>) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<BatterSingleSessionPitchByPitchStatsChartColumn | '' | 'id'>('');

  const rows = useMemo(
    () =>
      chartData?.pitchByPitchStats.map((row, i) => ({
        ...row,
        id: i.toString(),
      })) ?? [],
    [chartData?.pitchByPitchStats]
  );

  const columns = useMemo(() => {
    const displayedColumns = getDisplayedColumns<BatterSingleSessionPitchByPitchStatsChartColumn>(chartConfig.visibleStats);

    return displayedColumns.map((column) => ({
      ...tableHeaders[column],
      field: column,
      headerName: t(column),
      headerShortName: t('short.' + column),
      sortable: true,
      renderCell:
        column === 'pitchCall'
          ? (cellValue) => t(cellValue)
          : column === 'hitResult'
            ? (cellValue) => (cellValue === 'Undefined' ? '-' : t(cellValue))
            : column === 'pitchLocation'
              ? (cellValue, row: BatterSingleSessionPitchByPitchStatsDto) => (
                  <PitchLocationTableCell pitchLocation={cellValue} pitchType={row.pitchType} playerType='Batter' />
                )
              : column === 'pitchType'
                ? (cellValue) => <PitchTypeTableCell pitchType={cellValue} />
                : undefined,
    }));
  }, [chartConfig.visibleStats]);

  return (
    <Table
      rows={rows}
      pageState={[page, setPage]}
      orderState={[order, setOrder]}
      orderByState={[orderBy, setOrderBy]}
      columns={columns}
      loading={isLoading}
      noFooter
      tileTable
    />
  );
};

export default BatterSingleSessionPitchByPitchStatsChart;
